/**
 * Color Design Tokens
 * Comprehensive color palette with semantic colors, gradients, and overlays
 * Based on DESIGN_TOKENS.md specification
 */

export const colors = {
  // Primary Colors - Dark Slate for Professional Monitoring
  primary: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#0f172a',    // Primary brand color - Dark slate
    600: '#0f172a',
    700: '#0f172a',
    800: '#0f172a',
    900: '#020617',
  },

  // Secondary Colors - Medium Slate for Surfaces
  secondary: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#1e293b',    // Secondary brand - Medium slate
    600: '#1e293b',
    700: '#1e293b',
    800: '#1e293b',
    900: '#0f172a',
  },

  // Success Colors
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',    // Success brand
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#145231',
  },

  // Warning Colors
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',    // Warning brand
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },

  // Error Colors
  error: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',    // Error brand
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },

  // Info Colors - Professional Blue for CTAs
  info: {
    50: '#f0f9ff',
    100: '#e0f2fe',
    200: '#bae6fd',
    300: '#7dd3fc',
    400: '#38bdf8',
    500: '#0369a1',    // Info brand - CTA blue
    600: '#0284c7',
    700: '#0369a1',
    800: '#075985',
    900: '#0c4a6e',
  },

  // Neutral/Gray Colors
  gray: {
    0: '#ffffff',
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
    1000: '#000000',
  },

  // Gradients
  gradients: {
    primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    primaryReverse: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
    accent: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    accentReverse: 'linear-gradient(135deg, #00f2fe 0%, #4facfe 100%)',
    vibrant: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    vibrantReverse: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
    warm: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    cool: 'linear-gradient(135deg, #667eea 0%, #00f2fe 100%)',
  },

  // Overlay Colors
  overlays: {
    glassLight: 'rgba(255, 255, 255, 0.7)',
    glassDark: 'rgba(15, 23, 42, 0.7)',
    backdrop: 'rgba(0, 0, 0, 0.5)',
    dim: 'rgba(0, 0, 0, 0.3)',
  },

  // Dark Mode Colors
  dark: {
    bg: '#020617',      // Darker background for OLED
    surface: '#0f172a', // Primary slate
    surfaceElevated: '#1e293b', // Secondary slate
    border: '#334155',
    borderSubtle: '#1e293b',
    text: '#f8fafc',
    textMuted: '#94a3b8',
  },

  // Semantic Colors (Light Mode)
  semantic: {
    background: '#ffffff',
    surface: '#f9fafb',
    surfaceElevated: '#ffffff',
    text: '#1f2937',
    textSecondary: '#6b7280',
    textMuted: '#9ca3af',
    border: '#e5e7eb',
    borderSubtle: '#f3f4f6',
  },

  // Glassmorphism Colors
  glass: {
    light: {
      bg: 'rgba(255, 255, 255, 0.7)',
      bgSubtle: 'rgba(255, 255, 255, 0.4)',
      border: 'rgba(255, 255, 255, 0.2)',
      borderStrong: 'rgba(255, 255, 255, 0.4)',
    },
    dark: {
      bg: 'rgba(30, 41, 59, 0.8)',
      bgSubtle: 'rgba(30, 41, 59, 0.5)',
      border: 'rgba(148, 163, 184, 0.15)',
      borderStrong: 'rgba(148, 163, 184, 0.25)',
    },
  },

  // Status Glow Effects
  glow: {
    success: '0 0 20px rgba(34, 197, 94, 0.4)',
    warning: '0 0 20px rgba(245, 158, 11, 0.4)',
    error: '0 0 20px rgba(239, 68, 68, 0.4)',
    info: '0 0 20px rgba(59, 130, 246, 0.4)',
    primary: '0 0 20px rgba(102, 126, 234, 0.4)',
    successSubtle: '0 0 12px rgba(34, 197, 94, 0.25)',
    warningSubtle: '0 0 12px rgba(245, 158, 11, 0.25)',
    errorSubtle: '0 0 12px rgba(239, 68, 68, 0.25)',
  },

  // Status Background Colors (for badges/chips)
  statusBg: {
    success: 'rgba(34, 197, 94, 0.1)',
    warning: 'rgba(245, 158, 11, 0.1)',
    error: 'rgba(239, 68, 68, 0.1)',
    info: 'rgba(59, 130, 246, 0.1)',
    neutral: 'rgba(107, 114, 128, 0.1)',
  },
};

export type Colors = typeof colors;
export type ColorScheme = keyof typeof colors;
export type ColorValue = string;
